import React, { useState, useMemo } from 'react';
import ExportModal from 'src/components/Modals/ExportModal';
import { SlDropdownMenu } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/input';
import { ExportCheckBox } from 'src/components/BeaconV2Components/common/AppExportModal/AppExportModal';
import {
  useBusinessUnitEntity,
  useCategoryEntity,
  useRetailerId,
  useMainEntity,
  useSegmentEntity,
  useRetailerDisplayName,
  useMainEntityType
} from 'src/utils/Hooks/reduxSelectors';
import {
  EXPORT_TYPES,
  EXPORT_TYPES_DROPDOWN_OPTIONS,
  ExportType
} from 'src/components/AtlasV2Components/AtlasDorisExportComponents/Consts';
import { useAppSelector } from 'src/utils/Hooks';
import {
  DorisMetricEndPointEnum,
  DorisMetricTypeEnum
} from 'src/components/AtlasV2Components/Consts/DorisFieldNameEnum';
import { useDorisExportAction } from 'src/components/AtlasV2Components/AtlasDorisExportComponents/DorisExportHooks';

export interface SalesDropdownOption {
  id: ExportType;
  label: string;
  fieldName: string;
  excludeSeparateByWeek?: boolean;
  excludeComparison?: boolean;
  hideCustomizeSection?: boolean;
  fileName?: string;
}

// This takes almost every sales export, except content score , ratings and promotions. of these 3, custom dropdown options are defined in the each product grid.
export const useSalesDropdownOptions = (): SalesDropdownOption[] => {
  const { isAllCategoriesEntity } = useCategoryEntity();
  const entityType = useMainEntityType();

  switch (entityType) {
    case 'brand':
      return [
        EXPORT_TYPES_DROPDOWN_OPTIONS[EXPORT_TYPES.PRODUCT],
        EXPORT_TYPES_DROPDOWN_OPTIONS[EXPORT_TYPES.CATEGORY],
        EXPORT_TYPES_DROPDOWN_OPTIONS[EXPORT_TYPES.SUBCATEGORY]
      ];
    case 'category':
      return [
        EXPORT_TYPES_DROPDOWN_OPTIONS[EXPORT_TYPES.PRODUCT],
        EXPORT_TYPES_DROPDOWN_OPTIONS[EXPORT_TYPES.BRAND],
        ...(isAllCategoriesEntity ? [EXPORT_TYPES_DROPDOWN_OPTIONS[EXPORT_TYPES.CATEGORY]] : []),
        EXPORT_TYPES_DROPDOWN_OPTIONS[EXPORT_TYPES.SUBCATEGORY],
        EXPORT_TYPES_DROPDOWN_OPTIONS[EXPORT_TYPES.BRAND_SUBCATEGORY]
      ];

    case 'subcategory':
      return [EXPORT_TYPES_DROPDOWN_OPTIONS[EXPORT_TYPES.PRODUCT], EXPORT_TYPES_DROPDOWN_OPTIONS[EXPORT_TYPES.BRAND]];
    case 'product':
      return [EXPORT_TYPES_DROPDOWN_OPTIONS[EXPORT_TYPES.PRODUCT]];
    case 'segment':
    case 'businessunit':
      return [
        EXPORT_TYPES_DROPDOWN_OPTIONS[EXPORT_TYPES.PRODUCT],
        EXPORT_TYPES_DROPDOWN_OPTIONS[EXPORT_TYPES.BRAND],
        EXPORT_TYPES_DROPDOWN_OPTIONS[EXPORT_TYPES.CATEGORY],
        EXPORT_TYPES_DROPDOWN_OPTIONS[EXPORT_TYPES.SUBCATEGORY],
        EXPORT_TYPES_DROPDOWN_OPTIONS[EXPORT_TYPES.BRAND_SUBCATEGORY]
      ];
    case 'microtrend':
      return [
        EXPORT_TYPES_DROPDOWN_OPTIONS[EXPORT_TYPES.PRODUCT],
        EXPORT_TYPES_DROPDOWN_OPTIONS[EXPORT_TYPES.BRAND],
        EXPORT_TYPES_DROPDOWN_OPTIONS[EXPORT_TYPES.SUBCATEGORY]
      ];
    default:
      return [
        EXPORT_TYPES_DROPDOWN_OPTIONS[EXPORT_TYPES.PRODUCT],
        EXPORT_TYPES_DROPDOWN_OPTIONS[EXPORT_TYPES.BRAND],
        EXPORT_TYPES_DROPDOWN_OPTIONS[EXPORT_TYPES.CATEGORY],
        EXPORT_TYPES_DROPDOWN_OPTIONS[EXPORT_TYPES.SUBCATEGORY],
        EXPORT_TYPES_DROPDOWN_OPTIONS[EXPORT_TYPES.BRAND_SUBCATEGORY]
      ];
  }
};

export default function AtlasSalesExportModal({
  open,
  onClose,
  metricType,
  customDropdownOptions
}: {
  open: boolean;
  onClose: () => void;
  metricType?: DorisMetricTypeEnum;
  customDropdownOptions?: SalesDropdownOption[];
}) {
  const [isSeparatedByWeek, setIsSeparatedByWeek] = useState(false);
  const [isIncludeComparison, setIsIncludeComparison] = useState(false);
  const { name: mainEntityName, entityType: mainEntityType } = useMainEntity();
  const { isSegmentEntity } = useSegmentEntity();
  const { isBusinessUnitEntity } = useBusinessUnitEntity();
  const defaultDropdownOptions = useSalesDropdownOptions();
  const dropdownOptions = customDropdownOptions || defaultDropdownOptions;
  const [selectedGroupBy, setSelectedGroupBy] = useState(dropdownOptions[0]);
  const queryParams = useAppSelector((state) => state.app.queryParams);
  const { tab, subtab, sw, ew } = useAppSelector((state) => state.app.queryParams);
  const retailerId = useRetailerId();
  const retailerName = useRetailerDisplayName();
  const { startWeek, endWeek } = useAppSelector((state) => state.mainTimePeriod);
  const { startWeek: startComparisonWeek, endWeek: endComparisonWeek } = useAppSelector(
    (state) => state.comparisonTimePeriod
  );

  const exportParams = useMemo(
    () => ({
      exportType: selectedGroupBy?.id,
      ...(selectedGroupBy?.fieldName && { fieldName: selectedGroupBy?.fieldName }),
      metricType: metricType || subtab,
      shouldGroupByWeek: isSeparatedByWeek && !selectedGroupBy?.excludeSeparateByWeek,
      retailerId: +retailerId,
      startWeekId: startWeek,
      endWeekId: endWeek,
      fileName: (() => {
        const tabLabel = subtab && subtab !== 'keymetrics' ? subtab.replace(' ', '') : tab.replace('V2', '');
        const raw = `${mainEntityName}_${tabLabel}_${retailerName}_${sw}_${ew}`;
        const sanitized = raw.replace(/Amazon/g, 'Amz').replace(/_+/g, '_');
        const suffix = `_${sw}_${ew}`;
        return sanitized.length > 150 ? `${sanitized.slice(0, 150 - suffix.length)}${suffix}` : sanitized;
      })(),
      comparisonStartWeekId: isIncludeComparison ? startComparisonWeek : undefined,
      comparisonEndWeekId: isIncludeComparison ? endComparisonWeek : undefined,
      ...(isSegmentEntity || isBusinessUnitEntity
        ? { entityType: String(mainEntityType).toUpperCase(), entityName: mainEntityName }
        : {})
    }),
    [
      selectedGroupBy?.id,
      selectedGroupBy?.fieldName,
      selectedGroupBy?.excludeSeparateByWeek,
      metricType,
      subtab,
      isSeparatedByWeek,
      retailerId,
      startWeek,
      endWeek,
      mainEntityName,
      tab,
      retailerName,
      sw,
      ew,
      isIncludeComparison,
      startComparisonWeek,
      endComparisonWeek,
      isSegmentEntity,
      isBusinessUnitEntity,
      mainEntityType
    ]
  );

  const { handleExportClick, isExporting } = useDorisExportAction({
    endpoint: DorisMetricEndPointEnum.SalesExportEndPoint,
    params: exportParams,
    onClose
  });

  return (
    <ExportModal open={open} onClose={onClose}>
      <ExportModal.Header title="Export" onClose={onClose} />
      <ExportModal.Body
        isSeparatedByWeek={isSeparatedByWeek}
        isIncludeComparison={isIncludeComparison}
        setIsSeparatedByWeek={setIsSeparatedByWeek}
        setIsIncludeComparison={setIsIncludeComparison}
        hideCustomizeSection={selectedGroupBy?.hideCustomizeSection}
        customTimePeriodComponent={
          <>
            {!selectedGroupBy?.excludeSeparateByWeek && (
              <ExportCheckBox
                isSelected={isSeparatedByWeek}
                id="separateDataByWeek"
                label="Separate data by week"
                onSelect={(e) => {
                  setIsSeparatedByWeek(e.target.checked);
                }}
              />
            )}
            {!selectedGroupBy?.excludeComparison && (
              <ExportCheckBox
                isSelected={isIncludeComparison}
                id="includeComparison"
                label="Include comparison time period"
                onSelect={(e) => setIsIncludeComparison(e.target.checked)}
              />
            )}
          </>
        }
      >
        <SlDropdownMenu
          options={dropdownOptions}
          onChange={(option) =>
            setSelectedGroupBy(
              option as { id: ExportType; label: string; fieldName: string; excludeSeparateByWeek?: boolean }
            )
          }
          selectedId={selectedGroupBy.id}
        />
      </ExportModal.Body>
      <ExportModal.Footer
        searchParams={queryParams.searchParams}
        handleClose={onClose}
        handleExportClick={handleExportClick}
        exportButtonDisabled={isExporting}
      />
    </ExportModal>
  );
}
